<template>
  <section class="premium-delivery-admin">
    <div class="premium-delivery-admin__status" :class="{ 'premium-delivery-admin__status--complete': delivery }">
      <v-icon size="22" :color="deliveryLoadError ? '#b42318' : delivery ? '#ae7a22' : '#8a817b'">
        {{ deliveryLoadError ? "mdi-alert-circle-outline" : delivery ? "mdi-check-circle" : "mdi-clock-outline" }}
      </v-icon>
      <strong v-if="loadingDelivery">기존 전달 상태 확인 중</strong>
      <strong v-else-if="deliveryLoadError">기존 전달 상태를 확인하지 못했습니다</strong>
      <strong v-else-if="delivery">전달 완료</strong>
      <strong v-else>전달 전</strong>
    </div>

    <section v-if="deliveredCompleted.length" class="premium-delivery-admin__existing">
      <div class="premium-delivery-admin__existing-heading">
        <strong>전달된 사진 <small>{{ deliveredCompleted.length }}장</small></strong>
      </div>
      <ul class="premium-delivery-admin__existing-list">
        <li v-for="asset in deliveredCompleted" :key="asset.id" :title="asset.name">
          <img :src="asset.thumbnail_url || asset.url" :alt="asset.name || '전달된 완성 사진'" />
          <v-btn
            icon
            x-small
            color="#9d2d36"
            class="premium-delivery-admin__delete"
            :loading="deletingAssetId === asset.id"
            :disabled="uploading || Boolean(deletingAssetId)"
            :aria-label="`${asset.name || '완성 사진'} 삭제`"
            @click="deletePublished(asset)"
          >
            <v-icon size="18">mdi-delete-outline</v-icon>
          </v-btn>
        </li>
      </ul>
    </section>

    <div class="premium-delivery-admin__drop-grid">
      <article
        class="premium-dropzone"
        :class="{ 'premium-dropzone--active': draggingOriginals }"
        @dragover.prevent="draggingOriginals = true"
        @dragleave.prevent="draggingOriginals = false"
        @drop.prevent.stop="onDropOriginals"
        @click="triggerOriginalInput"
        @keydown.enter.prevent="triggerOriginalInput"
        @keydown.space.prevent="triggerOriginalInput"
        role="button"
        tabindex="0"
        aria-label="원본 사진 파일 선택"
        title="새 ZIP을 선택하면 기존 원본을 교체합니다."
      >
        <input
          ref="originalInput"
          class="premium-dropzone__input"
          type="file"
          accept=".zip,application/zip"
          @change="onOriginalInput"
          :disabled="uploading"
        />
        <v-icon size="40" color="#7d1832">mdi-file-image-outline</v-icon>
        <h4>원본 ZIP</h4>
        <strong v-if="originals.length">{{ originals[0].name }}</strong>
        <v-btn outlined color="#7d1832" class="premium-dropzone__select" :disabled="uploading" @click.stop="triggerOriginalInput">파일 선택</v-btn>

        <ul v-if="originals.length" class="premium-dropzone__files" @click.stop>
          <li v-for="(file, index) in originals" :key="`${file.name}-${file.size}-${index}`">
            <span>
              <b>{{ file.name }}</b>
              <small>{{ formatFileSize(file.size) }}</small>
            </span>
            <v-btn icon x-small :disabled="uploading" :aria-label="`${file.name} 삭제`" @click.stop="removeOriginal(index)">
              <v-icon size="18">mdi-close</v-icon>
            </v-btn>
          </li>
        </ul>
      </article>

      <article
        class="premium-dropzone"
        :class="{ 'premium-dropzone--active': draggingCompleted }"
        @dragover.prevent="draggingCompleted = true"
        @dragleave.prevent="draggingCompleted = false"
        @drop.prevent.stop="onDropCompleted"
        @click="triggerCompletedInput"
        @keydown.enter.prevent="triggerCompletedInput"
        @keydown.space.prevent="triggerCompletedInput"
        role="button"
        tabindex="0"
        aria-label="완성 사진 파일 선택"
        title="새 사진은 기존 사진을 유지한 채 추가됩니다."
      >
        <input
          ref="completedInput"
          class="premium-dropzone__input"
          type="file"
          multiple
          accept=".jpg,.jpeg,image/jpeg"
          @change="onCompletedInput"
          :disabled="uploading"
        />
        <v-icon size="40" color="#7d1832">mdi-file-check-outline</v-icon>
        <h4>완성 사진</h4>
        <strong v-if="completed.length">{{ completed.length }}장 추가</strong>
        <v-btn outlined color="#7d1832" class="premium-dropzone__select" :disabled="uploading" @click.stop="triggerCompletedInput">파일 추가</v-btn>

        <ul v-if="completed.length" class="premium-dropzone__files" @click.stop>
          <li v-for="(file, index) in completed" :key="`${file.name}-${file.size}-${index}`">
            <span>
              <b>{{ file.name }}</b>
              <small>{{ formatFileSize(file.size) }}</small>
            </span>
            <v-btn icon x-small :disabled="uploading" :aria-label="`${file.name} 삭제`" @click.stop="removeCompleted(index)">
              <v-icon size="18">mdi-close</v-icon>
            </v-btn>
          </li>
        </ul>
      </article>
    </div>

    <section v-if="uploading || uploadFailed" class="premium-delivery-admin__progress" aria-live="polite">
      <div class="premium-delivery-admin__progress-copy">
        <strong>{{ uploading ? `고객에게 전달 중 ${uploadPercent}%` : "전달이 중단되었습니다" }}</strong>
        <span v-if="uploading">{{ formatFileSize(uploadLoadedBytes) }} / {{ formatFileSize(uploadTotalBytes || selectedTotalBytes) }}</span>
        <span v-else>선택한 파일은 그대로 남아 있습니다. 다시 시도할 수 있습니다.</span>
      </div>
      <v-progress-linear
        :value="uploadPercent"
        :color="uploadFailed ? '#b42318' : '#7d1832'"
        height="8"
        rounded
      />
      <v-btn v-if="uploading" text small color="#7d1832" class="premium-delivery-admin__cancel" @click="cancelUpload">전송 취소</v-btn>
    </section>

    <v-alert v-if="errorMessage" dense outlined type="error" class="mt-4 mb-0">
      {{ errorMessage }}
    </v-alert>

    <div v-if="showAction" class="premium-delivery-admin__footer">
      <v-btn
        depressed
        color="primary"
        class="premium-delivery-admin__publish"
        :loading="uploading"
        :disabled="!canPublish"
        @click="publish"
      >
        {{ publishButtonLabel }}
      </v-btn>
    </div>
  </section>
</template>

<script>
export default {
  name: "PremiumPhotoDeliveryUpload",
  props: {
    reservation: {
      type: Object,
      required: true,
    },
    showAction: {
      type: Boolean,
      default: true,
    },
  },
  data: () => ({
    originals: [],
    completed: [],
    delivery: null,
    uploading: false,
    loadingDelivery: false,
    deliveryLoadError: false,
    deliveryRequestId: 0,
    draggingOriginals: false,
    draggingCompleted: false,
    errorMessage: "",
    uploadPercent: 0,
    uploadLoadedBytes: 0,
    uploadTotalBytes: 0,
    uploadFailed: false,
    uploadCancelSource: null,
    deletingAssetId: null,
  }),
  computed: {
    deliveredOriginal() {
      return this.delivery && Array.isArray(this.delivery.originals)
        ? this.delivery.originals[0] || null
        : null
    },
    deliveredCompleted() {
      return this.delivery && Array.isArray(this.delivery.completed)
        ? this.delivery.completed
        : []
    },
    canPublish() {
      const hasSelectedFiles = this.originals.length === 1 || this.completed.length > 0
      const willHaveCompleted =
        this.completed.length > 0 || this.deliveredCompleted.length > 0
      return (
        hasSelectedFiles &&
        willHaveCompleted &&
        !this.uploading &&
        !this.deletingAssetId &&
        !this.loadingDelivery &&
        !this.deliveryLoadError
      )
    },
    publishButtonLabel() {
      if (this.uploading) return `전달 중 ${this.uploadPercent}%`
      if (this.uploadFailed) return "다시 전달 시도"
      return this.delivery ? "선택 파일 추가 전달" : "고객에게 전달"
    },
    selectedOriginalBytes() {
      return this.originals.reduce((sum, file) => sum + Number(file.size || 0), 0)
    },
    selectedCompletedBytes() {
      return this.completed.reduce((sum, file) => sum + Number(file.size || 0), 0)
    },
    selectedTotalBytes() {
      return this.selectedOriginalBytes + this.selectedCompletedBytes
    },
  },
  watch: {
    "reservation.id": {
      immediate: true,
      handler() {
        this.resetSelection()
        this.loadDelivery()
      },
    },
  },
  mounted() {
    window.addEventListener("beforeunload", this.preventPageExit)
    this.notifyState()
  },
  beforeDestroy() {
    window.removeEventListener("beforeunload", this.preventPageExit)
    this.cancelUpload(false)
  },
  methods: {
    async loadDelivery() {
      if (!this.reservation || !this.reservation.id) return
      const reservationId = this.reservation.id
      const requestId = ++this.deliveryRequestId
      this.loadingDelivery = true
      this.deliveryLoadError = false
      try {
        const { data } = await this.$http.get(
          `/api/admin/photo-deliveries/by-reservation/${reservationId}`
        )
        if (requestId !== this.deliveryRequestId || reservationId !== this.reservation.id) return
        this.delivery = data.delivery || null
      } catch (error) {
        if (requestId !== this.deliveryRequestId || reservationId !== this.reservation.id) return
        this.delivery = null
        this.deliveryLoadError = true
      } finally {
        if (requestId !== this.deliveryRequestId || reservationId !== this.reservation.id) return
        this.loadingDelivery = false
        this.notifyState()
      }
    },
    resetSelection() {
      this.originals = []
      this.completed = []
      this.errorMessage = ""
      this.resetUploadProgress()
      if (this.$refs.originalInput) this.$refs.originalInput.value = ""
      if (this.$refs.completedInput) this.$refs.completedInput.value = ""
      this.notifyState()
    },
    triggerOriginalInput() {
      if (!this.uploading && this.$refs.originalInput) this.$refs.originalInput.click()
    },
    triggerCompletedInput() {
      if (!this.uploading && this.$refs.completedInput) this.$refs.completedInput.click()
    },
    onDropOriginals(event) {
      this.draggingOriginals = false
      if (this.uploading) return
      this.setOriginals(event.dataTransfer.files)
    },
    onDropCompleted(event) {
      this.draggingCompleted = false
      if (this.uploading) return
      this.setCompleted(event.dataTransfer.files)
    },
    onOriginalInput(event) {
      this.setOriginals(event.target.files)
      event.target.value = ""
    },
    onCompletedInput(event) {
      this.setCompleted(event.target.files)
      event.target.value = ""
    },
    setOriginals(fileList) {
      const files = Array.from(fileList || [])
      const valid = files.length === 1 && files[0].name.toLowerCase().endsWith(".zip")
      this.originals = valid ? [files[0]] : []
      this.errorMessage = valid ? "" : "원본 전체가 담긴 ZIP 파일 1개만 등록해 주세요."
      this.uploadFailed = false
      this.notifyState()
    },
    setCompleted(fileList) {
      const incoming = Array.from(fileList || []).filter((file) =>
        [".jpg", ".jpeg"].some((extension) => file.name.toLowerCase().endsWith(extension))
      )
      const known = new Set(this.completed.map(this.fileIdentity))
      const unique = incoming.filter((file) => {
        const identity = this.fileIdentity(file)
        if (known.has(identity)) return false
        known.add(identity)
        return true
      })
      this.completed = this.completed.concat(unique)
      this.errorMessage = incoming.length ? "" : "완성 사진은 JPG 파일만 등록할 수 있습니다."
      this.uploadFailed = false
      this.notifyState()
    },
    fileIdentity(file) {
      return [file.name, file.size, file.lastModified || 0].join("::")
    },
    removeOriginal(index) {
      if (this.uploading) return
      this.originals = this.originals.filter((file, fileIndex) => fileIndex !== index)
      if (this.$refs.originalInput) this.$refs.originalInput.value = ""
      this.notifyState()
    },
    removeCompleted(index) {
      if (this.uploading) return
      this.completed = this.completed.filter((file, fileIndex) => fileIndex !== index)
      if (this.$refs.completedInput) this.$refs.completedInput.value = ""
      this.notifyState()
    },
    clearSelection() {
      if (this.uploading) return
      this.resetSelection()
    },
    formatFileSize(size) {
      if (!Number.isFinite(size) || size <= 0) return "0 KB"
      if (size < 1024 * 1024) return `${Math.max(1, Math.round(size / 1024))} KB`
      return `${(size / (1024 * 1024)).toFixed(1)} MB`
    },
    notifyState() {
      this.$nextTick(() => {
        this.$emit("state-change", {
          canPublish: this.canPublish,
          uploading: this.uploading,
          uploadPercent: this.uploadPercent,
          buttonLabel: this.publishButtonLabel,
        })
      })
    },
    resetUploadProgress() {
      this.uploadPercent = 0
      this.uploadLoadedBytes = 0
      this.uploadTotalBytes = 0
      this.uploadFailed = false
      this.uploadCancelSource = null
    },
    updateUploadProgress(event) {
      const total = Number(event && event.total) || this.selectedTotalBytes
      const loaded = Math.min(Number(event && event.loaded) || 0, total || Number.MAX_SAFE_INTEGER)
      this.uploadTotalBytes = total
      this.uploadLoadedBytes = loaded
      this.uploadPercent = total > 0 ? Math.min(100, Math.round((loaded / total) * 100)) : 0
      this.notifyState()
    },
    preventPageExit(event) {
      if (!this.uploading) return undefined
      event.preventDefault()
      event.returnValue = ""
      return ""
    },
    cancelUpload(showMessage = true) {
      if (this.uploadCancelSource) {
        this.uploadCancelSource.cancel("UPLOAD_CANCELLED")
        this.uploadCancelSource = null
      }
      if (showMessage && this.uploading) {
        this.errorMessage = "전송을 취소했습니다. 선택한 파일은 그대로 남아 있습니다."
      }
    },
    async publish() {
      if (!this.canPublish) {
        if (this.loadingDelivery) {
          this.errorMessage = "기존 전달 상태를 확인하고 있습니다. 잠시 후 다시 눌러 주세요."
        } else if (this.deliveryLoadError) {
          this.errorMessage = "기존 전달 상태를 확인하지 못해 전달할 수 없습니다. 예약 창을 다시 열어 주세요."
        } else if (!this.originals.length && !this.completed.length) {
          this.errorMessage = "추가할 ZIP 또는 완성 JPG 파일을 선택해 주세요."
        } else if (!this.delivery && !this.completed.length) {
          this.errorMessage = "첫 전달에는 완성 JPG 파일을 1개 이상 등록해 주세요."
        }
        return
      }
      const confirmation = this.originals.length && this.completed.length
        ? `원본 ZIP과 완성사진 ${this.completed.length}장을 고객에게 전달할까요? 기존 완성사진은 유지됩니다.`
        : this.originals.length
          ? "원본 ZIP을 새 파일로 교체할까요? 기존 완성사진은 유지됩니다."
          : `완성사진 ${this.completed.length}장을 기존 사진 뒤에 추가할까요?`
      if (typeof window !== "undefined" && !window.confirm(confirmation)) {
        return
      }
      const formData = new FormData()
      formData.append("reservation_id", this.reservation.id)
      this.originals.forEach((file) => formData.append("originals", file))
      this.completed.forEach((file) => formData.append("completed", file))
      this.uploading = true
      this.uploadFailed = false
      this.uploadPercent = 0
      this.uploadLoadedBytes = 0
      this.uploadTotalBytes = this.selectedTotalBytes
      this.uploadCancelSource = this.$http.CancelToken.source()
      this.errorMessage = ""
      this.notifyState()
      try {
        const { data } = await this.$http.post("/api/admin/photo-deliveries", formData, {
          cancelToken: this.uploadCancelSource.token,
          onUploadProgress: this.updateUploadProgress,
        })
        this.uploadPercent = 100
        this.uploadLoadedBytes = this.uploadTotalBytes
        this.delivery = data.delivery
        this.resetSelection()
        this.$emit("published", this.delivery)
        this.$toast.open({
          message: "고객 마이페이지에 사진을 전달했습니다.",
          type: "success",
        })
      } catch (error) {
        const cancelled = this.$http.isCancel && this.$http.isCancel(error)
        this.uploadFailed = true
        const code = error.response && error.response.data && error.response.data.error
        this.errorMessage = cancelled
          ? "전송을 취소했습니다. 선택한 파일은 그대로 남아 있습니다."
          :
          code === "UPLOAD_FILES_REQUIRED"
            ? "추가할 ZIP 또는 완성 JPG 파일을 선택해 주세요."
            : code === "ORIGINAL_ZIP_MUST_BE_SINGLE_FILE" || code === "ORIGINAL_ZIP_REQUIRED"
              ? "원본 전체가 담긴 ZIP 파일 1개만 등록해 주세요."
              : code === "COMPLETED_JPEG_REQUIRED" || code === "INVALID_COMPLETED_JPEG"
              ? "완성 사진은 정상적인 JPG 파일만 등록해 주세요."
              : code === "ORIGINAL_TOO_LARGE"
                ? "원본 ZIP 파일 용량이 너무 큽니다."
                : code === "COMPLETED_FILE_TOO_LARGE" || code === "COMPLETED_TOTAL_TOO_LARGE"
                  ? "완성 사진 용량이 너무 큽니다."
                  : code === "DELIVERY_PUBLISH_IN_PROGRESS"
                    ? "같은 예약의 사진을 전달하고 있습니다. 잠시 후 다시 시도해 주세요."
            : "사진을 전달하지 못했습니다. 파일을 확인한 뒤 다시 시도해 주세요."
      } finally {
        this.uploading = false
        this.uploadCancelSource = null
        this.notifyState()
      }
    },
    async deletePublished(asset) {
      if (!asset || !asset.id || this.uploading || this.deletingAssetId) return
      if (this.deliveredCompleted.length <= 1) {
        this.errorMessage = "마지막 완성사진은 삭제할 수 없습니다. 새 사진을 먼저 추가해 주세요."
        return
      }
      if (
        typeof window !== "undefined" &&
        !window.confirm(`완성사진 ‘${asset.name || "이름 없음"}’을 고객 마이페이지에서도 삭제할까요?`)
      ) {
        return
      }
      this.deletingAssetId = asset.id
      this.errorMessage = ""
      this.notifyState()
      try {
        const { data } = await this.$http.delete(
          `/api/admin/photo-deliveries/${this.reservation.id}/completed/${asset.id}`
        )
        this.delivery = data.delivery
        this.$emit("published", this.delivery)
        this.$toast.open({
          message: "완성사진을 삭제했습니다.",
          type: "success",
        })
      } catch (error) {
        const code = error.response && error.response.data && error.response.data.error
        this.errorMessage = code === "LAST_COMPLETED_PHOTO_REQUIRED"
          ? "마지막 완성사진은 삭제할 수 없습니다. 새 사진을 먼저 추가해 주세요."
          : "완성사진을 삭제하지 못했습니다. 다시 시도해 주세요."
      } finally {
        this.deletingAssetId = null
        this.notifyState()
      }
    },
  },
}
</script>

<style scoped>
.premium-delivery-admin {
  padding-top: 18px;
  color: #2d2926;
}

.premium-delivery-admin__status {
  display: flex;
  min-height: 38px;
  margin-bottom: 16px;
  color: #756c66;
  font-size: 13px;
  align-items: center;
  gap: 8px;
}

.premium-delivery-admin__status--complete {
  color: #98671d;
}

.premium-delivery-admin__existing {
  margin-bottom: 14px;
  padding: 13px;
  border: 1px solid #e4dcd7;
  border-radius: 9px;
  background: #fbf9f7;
}

.premium-delivery-admin__existing-heading {
  display: flex;
  margin-bottom: 10px;
  align-items: center;
}

.premium-delivery-admin__existing-heading strong {
  color: #3c3531;
  font-size: 13px;
}

.premium-delivery-admin__existing-heading small {
  margin-left: 5px;
  color: #786e68;
  font-size: 11px;
  font-weight: 600;
}

.premium-delivery-admin__existing-list {
  display: grid;
  max-height: 220px;
  margin: 0;
  padding: 0;
  grid-template-columns: repeat(3, 96px);
  gap: 10px;
  overflow-y: auto;
  list-style: none;
}

.premium-delivery-admin__existing-list li {
  position: relative;
  display: block;
  min-width: 0;
  border-radius: 8px;
  background: #f0ece9;
}

.premium-delivery-admin__existing-list img {
  display: block;
  width: 100%;
  aspect-ratio: 4 / 5;
  border-radius: 8px;
  background: #f0ece9;
  object-fit: cover;
}

.premium-delivery-admin__delete {
  position: absolute !important;
  top: 5px;
  right: 5px;
  width: 24px !important;
  height: 24px !important;
  background: rgba(255, 255, 255, 0.94) !important;
  box-shadow: 0 2px 8px rgba(42, 31, 27, 0.16);
}

.premium-delivery-admin__drop-grid {
  display: grid;
  min-width: 0;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
}

.premium-dropzone {
  overflow: hidden;
  box-sizing: border-box;
  width: 100%;
  min-width: 0;
  min-height: 226px;
  padding: 24px 20px 18px;
  border: 1px solid #cec6c0;
  border-radius: 9px;
  background: #fff;
  text-align: center;
  cursor: pointer;
  outline: 0;
  transition: border-color 0.18s ease, background 0.18s ease, box-shadow 0.18s ease;
}

.premium-dropzone:hover,
.premium-dropzone:focus-visible,
.premium-dropzone--active {
  border-color: #7d1832;
  background: #fffaf9;
  box-shadow: 0 0 0 3px rgba(125, 24, 50, 0.08);
}

.premium-dropzone__input {
  display: none;
}

.premium-dropzone h4 {
  margin: 11px 0 0;
  font-size: 20px;
  letter-spacing: -0.03em;
}

.premium-dropzone h4 small {
  display: inline-block;
  margin-left: 4px;
  color: #8b817a;
  font-size: 10px;
  font-weight: 600;
  vertical-align: middle;
}

.premium-dropzone > strong {
  display: block;
  overflow: hidden;
  width: 100%;
  min-width: 0;
  margin: 16px 0 12px;
  font-size: 14px;
  font-weight: 500;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.premium-dropzone__select {
  min-width: 116px !important;
  height: 42px !important;
  border-radius: 6px;
  font-weight: 700;
}

.premium-dropzone h4 + .premium-dropzone__select {
  margin-top: 18px;
}

.premium-dropzone__files {
  overflow-y: auto;
  width: 100%;
  min-width: 0;
  max-height: 104px;
  margin: 18px 0 0;
  padding: 8px;
  border-top: 1px solid #eee8e4;
  list-style: none;
  text-align: left;
}

.premium-dropzone__files li {
  display: flex;
  min-width: 0;
  padding: 6px 4px;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.premium-dropzone__files li + li {
  border-top: 1px solid #f1ece9;
}

.premium-dropzone__files span {
  display: flex;
  overflow: hidden;
  max-width: 100%;
  min-width: 0;
  flex: 1 1 auto;
  flex-direction: column;
}

.premium-dropzone__files b {
  overflow: hidden;
  font-size: 11px;
  font-weight: 600;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.premium-dropzone__files small {
  color: #817771;
  font-size: 10px;
}

.premium-delivery-admin__footer {
  display: flex;
  margin-top: 14px;
  color: #88766f;
  font-size: 11px;
  align-items: center;
  justify-content: flex-end;
  gap: 18px;
}

.premium-delivery-admin__selection {
  display: flex;
  min-height: 54px;
  margin-top: 12px;
  padding: 9px 12px;
  border: 1px solid #e2dad5;
  border-radius: 8px;
  background: #fbf9f7;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.premium-delivery-admin__selection > div {
  display: flex;
  min-width: 0;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px 14px;
}

.premium-delivery-admin__selection strong {
  color: #3c3531;
  font-size: 12px;
}

.premium-delivery-admin__selection span {
  color: #756b65;
  font-size: 11px;
}

.premium-delivery-admin__progress {
  position: relative;
  margin-top: 12px;
  padding: 14px 16px 13px;
  border: 1px solid #d9c9c1;
  border-radius: 9px;
  background: #fffaf8;
}

.premium-delivery-admin__progress-copy {
  display: flex;
  margin-bottom: 9px;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
}

.premium-delivery-admin__progress-copy strong {
  color: #4b3736;
  font-size: 13px;
}

.premium-delivery-admin__progress-copy span {
  color: #766a64;
  font-size: 11px;
}

.premium-delivery-admin__cancel {
  display: block;
  min-width: 0 !important;
  margin: 7px 0 -6px auto;
}

.premium-delivery-admin__published {
  color: #34745a;
}

.premium-delivery-admin__publish {
  min-width: 142px;
  border-radius: 10px;
  font-weight: 700;
}

@media (max-width: 720px) {
  .premium-delivery-admin__existing-list {
    grid-template-columns: repeat(2, 88px);
  }

  .premium-delivery-admin__drop-grid {
    grid-template-columns: 1fr;
  }

  .premium-delivery-admin__footer {
    align-items: stretch;
    flex-direction: column;
  }

  .premium-delivery-admin__selection,
  .premium-delivery-admin__progress-copy {
    align-items: flex-start;
    flex-direction: column;
  }

  .premium-dropzone {
    min-height: 210px;
  }
}
</style>
